import { React, useEffect, useState } from "react";
import { useTheme } from "../components/ThemeContext";
import { Button, Container, Table, Spinner } from "react-bootstrap";
import { IoMdEye } from "react-icons/io";
import NavMenu from "../components/NavMenu";
import axios from "axios";
import photo from "../img/no-data.svg";
import { useNavigate } from "react-router-dom";

function ViewAppraisal() {
  const [staff, setStaff] = useState([]);
  const [id, setID] = useState("");
  const [loading, setLoading] = useState(true);
  const [appraisalNames, setAppraisalNames] = useState([]);
  const [selectedAppraisal, setSelectedAppraisal] = useState("");
  const navigate = useNavigate();
  const { isDark } = useTheme();

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user?.id) {
      setID(user.id);
    }
  }, []);

  useEffect(() => {
    if (!id) return;

    const fetchStaff = async () => {
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("No auth token found in localStorage");
      }

      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_API}/getPAForm?supervisor_id=${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (response.data.success) {
          setStaff(response.data.data);
          const names = [
            ...new Set(response.data.data.map((item) => item.appraisal_name)),
          ];
          setAppraisalNames(names);
        } else {
          console.error(response.data.message);
        }
      } catch (error) {
        console.error("Error fetching staff data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStaff();
  }, [id]);

  const getStatusStyle = (status) => {
    switch ((status || "").toLowerCase()) {
      case "submitted":
        return {
          bg: "#e6ffe6",
          text: "#2e7d32",
          dot: "#4caf50",
        };
      case "employee signed":
        return {
          bg: "#d0f0fd",
          text: "#0077b6",
          dot: "#0096c7",
        };
      case "both signed":
        return {
          bg: "#f3e5f5",
          text: "#6a1b9a",
          dot: "#9c27b0",
        };
      case "reviewing":
        return {
          bg: "#fff3e0",
          text: "#ef6c00",
          dot: "#ff9800",
        };
      case "pending":
        return {
          bg: "#ffebee",
          text: "#c62828",
          dot: "#f44336",
        };
      default:
        return {
          bg: "#eceff1",
          text: "#546e7a",
          dot: "#90a4ae",
        };
    }
  };

  const handleSubmit = (role_id, send_id, unique_doc_id) => {
    // console.log("Selected role_id:", role_id, "send_id:", send_id, unique_doc_id);
    navigate(`/submit-appraisal/${role_id}`, {
      state: { send_id, unique_doc_id },
    });
  };

  return (
    <>
      <NavMenu />
      <Container
        fluid
        className={`min-vh-100 p-4 ${isDark ? "text-light" : "text-dark"}`}
        style={{ paddingBottom: "80px" }}
      >
        <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
          <h5 className="header-title mb-2 mb-md-0 fw-bold">View Appraisal</h5>

          <div className="d-flex gap-2">
            <select
              className={`form-select form-select-sm ${
                isDark ? "bg-dark text-light border-secondary" : ""
              }`}
              style={{ width: "200px" }}
              value={selectedAppraisal}
              onChange={(e) => setSelectedAppraisal(e.target.value)}
            >
              <option value="">All Appraisals</option>
              {appraisalNames.map((name, idx) => (
                <option key={idx} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>
        </div>
        {loading ? (
          <div className="text-center">
            <Spinner
              animation="border"
              variant={isDark ? "light" : "primary"}
            />
          </div>
        ) : (
          <Table
            hover
            responsive
            className={`align-middle ${isDark ? "table-dark" : ""}`}
          >
            <thead>
              <tr className="text-center">
                <th>No</th>
                <th>Name</th>
                <th>Department</th>
                <th>Appraisal</th>
                <th>Supervisor</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {staff.length > 0 ? (
                staff
                  .filter((emp) =>
                    selectedAppraisal
                      ? emp.appraisal_name === selectedAppraisal
                      : true
                  )
                  .map((emp, index) => (
                    <tr key={index} className="align-middle text-center">
                      <td>{index + 1}</td>
                      <td>{emp.staff_name || "-"}</td>
                      <td>{emp.department || "-"}</td>
                      <td>{emp.appraisal_name || "-"}</td>
                      <td>{emp.supervisor_name || "-"}</td>
                      <td>
                        {(() => {
                          const { bg, text, dot } = getStatusStyle(emp.status);
                          return (
                            <div
                              className="d-inline-flex align-items-center px-2 py-1 rounded-pill"
                              style={{
                                backgroundColor: bg,
                                color: text,
                                fontWeight: "500",
                                textTransform: "capitalize",
                                fontSize: "12px",
                              }}
                            >
                              <div
                                style={{
                                  width: "10px",
                                  height: "10px",
                                  backgroundColor: dot,
                                  borderRadius: "50%",
                                  marginRight: "8px",
                                }}
                              ></div>
                              {emp.status || "-"}
                            </div>
                          );
                        })()}
                      </td>
                      <td>
                        <div className="d-flex justify-content-center gap-2">
                          <Button
                            variant="outline-light"
                            size="sm"
                            style={{
                              color: "#B31E8D",
                              borderColor: "#B31E8D",
                              backgroundColor: isDark
                                ? "#2c2c2c"
                                : "transparent",
                            }}
                            onClick={() =>
                              handleSubmit(
                                emp.role_id,
                                emp.send_id,
                                emp.unique_doc_id
                              )
                            }
                          >
                            <IoMdEye size={20} />
                            &nbsp; View PA Assessment
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center py-4">
                    <div className="d-flex flex-column align-items-center">
                      <img
                        src={photo}
                        alt="Illustration"
                        className="img-fluid mb-3"
                        width="100px"
                      />
                      <div
                        className={` ${
                          isDark ? "text-secondary" : "text-muted"
                        }`}
                      >
                        No PA Form found
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        )}
      </Container>
    </>
  );
}

export default ViewAppraisal;
